import React from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Restaurant,
  TrendingUp,
  Speed,
  Security,
  SmartToy,
  GetApp,
  CheckCircleOutline,
  ArrowForward,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEngagementTracking } from '../hooks/useAnalytics';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Track engagement time on Landing page
  useEngagementTracking('Landing');

  const features = [
    {
      icon: <SmartToy sx={{ fontSize: 40, color: '#4ca6c9' }} />,
      title: 'AI-Powered Plans',
      description: 'Get personalized diet plans generated by advanced AI technology based on your unique profile and goals.',
    },
    {
      icon: <Restaurant sx={{ fontSize: 40, color: '#4ca6c9' }} />,
      title: 'Diverse Cuisines',
      description: 'Enjoy meals from various cultures and cuisines, tailored to your dietary preferences and restrictions.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: '#4ca6c9' }} />,
      title: 'Track Progress',
      description: 'Monitor your nutrition intake and progress towards your health and fitness goals with detailed analytics.',
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: '#4ca6c9' }} />,
      title: 'Quick & Easy',
      description: 'Generate comprehensive weekly meal plans in seconds with recipes, instructions, and shopping lists.',
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#4ca6c9' }} />,
      title: 'Safe & Secure',
      description: 'Your personal health data is protected with enterprise-grade security and privacy measures.',
    },
    {
      icon: <GetApp sx={{ fontSize: 40, color: '#4ca6c9' }} />,
      title: 'Export PDF',
      description: 'Download your meal plans as beautiful PDF documents to print or share with nutritionists.',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4ca6c9 0%, #3c89af 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center" sx={{ minHeight: { xs: 'auto', md: '85vh' }, py: { xs: 8, md: 12 } }}>
            <Grid item xs={12} md={6}>
              <Chip
                icon={<AutoAwesome sx={{ fontSize: 16 }} />}
                label="AI-Powered Nutrition"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  mb: 3,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Typography
                variant={isMobile ? 'h2' : 'h1'}
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: 'white',
                  lineHeight: 1.2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Transform Your Health with
                <Box component="span" sx={{ display: 'block', color: '#fff3e0' }}>
                  Personalized Meal Plans
                </Box>
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  color: 'rgba(255,255,255,0.95)',
                  fontWeight: 400,
                  lineHeight: 1.7,
                  maxWidth: 540,
                }}
              >
                Get AI-generated diet plans tailored to your goals, preferences, and lifestyle. 
                Achieve your health objectives with science-backed nutrition guidance.
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                {user ? (
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/dashboard')}
                    sx={{
                      bgcolor: 'white',
                      color: '#4ca6c9',
                      px: 4,
                      py: 1.75,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    Go to Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/create-plan')}
                      sx={{
                        bgcolor: 'white',
                        color: '#4ca6c9',
                        px: 4,
                        py: 1.75,
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                        },
                      }}
                    >
                      Get Started Free
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        borderWidth: 2,
                        px: 4,
                        py: 1.75,
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                  </>
                )}
              </Stack>

              <Stack direction="row" spacing={3} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutline sx={{ color: '#fff3e0', mr: 1, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    No credit card required
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutline sx={{ color: '#fff3e0', mr: 1, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Cancel anytime
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <Stack spacing={2}>
                  {['Weekly meal plans', 'Nutritional tracking', 'Recipe database', 'Shopping lists'].map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.95)',
                        borderRadius: 2,
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        transform: `translateX(${idx * 10}px)`,
                      }}
                    >
                      <CheckCircleOutline sx={{ color: '#4ca6c9', mr: 2, fontSize: 28 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#424242' }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#4ca6c9',
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.875rem',
            }}
          >
            FEATURES
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              color: '#212121',
              mt: 1,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Everything You Need to Succeed
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#757575',
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.125rem',
            }}
          >
            Comprehensive tools and features designed to make healthy eating simple and sustainable
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(76, 166, 201, 0.15)',
                    borderColor: '#4ca6c9',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'rgba(76, 166, 201, 0.1)',
                      mb: 3,
                    }}
                  >
                    {React.cloneElement(feature.icon as React.ReactElement, {
                      sx: { fontSize: 32, color: '#4ca6c9' },
                    })}
                  </Avatar>
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: '#212121',
                      mb: 2,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: 'white', py: { xs: 8, md: 12 }, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#4ca6c9',
                fontWeight: 700,
                letterSpacing: 2,
                fontSize: '0.875rem',
              }}
            >
              SIMPLE PROCESS
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                color: '#212121',
                mt: 1,
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}
            >
              How It Works
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                step: '01',
                title: 'Create Your Profile',
                description: 'Tell us about your age, weight, height, fitness goals, and dietary preferences or restrictions.',
                color: '#4ca6c9',
              },
              {
                step: '02',
                title: 'AI Generates Your Plan',
                description: 'Our advanced AI analyzes your profile and creates a personalized weekly meal plan with detailed recipes.',
                color: '#ff9800',
              },
              {
                step: '03',
                title: 'Track & Achieve',
                description: 'Follow your meal plan, track your progress, and watch as you achieve your health and fitness goals.',
                color: '#10b981',
              },
            ].map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    p: 4,
                    borderRadius: 3,
                    bgcolor: '#f8fafc',
                    border: '2px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: item.color,
                      transform: 'translateY(-4px)',
                      bgcolor: 'white',
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      fontSize: '5rem',
                      color: item.color,
                      opacity: 0.15,
                      position: 'absolute',
                      top: -20,
                      right: 20,
                      lineHeight: 1,
                    }}
                  >
                    {item.step}
                  </Typography>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: item.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                      {idx + 1}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: '#212121',
                      mb: 2,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', lineHeight: 1.7 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4ca6c9 0%, #3c89af 100%)',
          py: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              color: 'white',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Ready to Transform Your Health?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: 'rgba(255,255,255,0.95)',
              fontWeight: 400,
              lineHeight: 1.7,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Join thousands of users achieving their health goals with personalized, 
            AI-powered nutrition plans. Start your journey today—completely free.
          </Typography>
          {!user && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'white',
                  color: '#4ca6c9',
                  px: 5,
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                  },
                }}
              >
                Start Your Free Plan
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  borderWidth: 2,
                  px: 5,
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Learn More
              </Button>
            </Stack>
          )}
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#212121', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Diet Planner
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>
                Your AI-powered companion for achieving optimal health through personalized nutrition planning.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Features
              </Typography>
              <Stack spacing={1}>
                {['AI Meal Plans', 'Recipe Database', 'Progress Tracking', 'PDF Export'].map((item) => (
                  <Typography key={item} variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Company
              </Typography>
              <Stack spacing={1}>
                {['About Us', 'Privacy Policy', 'Terms of Service', 'Contact'].map((item) => (
                  <Typography key={item} variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ pt: 4, borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              © {new Date().getFullYear()} Diet Planner. All rights reserved. Powered by AI Technology.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
